import React, {type ReactElement} from "react";
import Dotdotdot from "react-dotdotdot";
import {v4 as uuid} from "uuid";

import Video, {VideoType} from "../../videos/video/Video";
import CTAButton, {CTA} from "../../buttons/cta-button/CTAButton";
import Image, {ImageObject} from "../../image/Image";
import Icon from "../../icon/Icon";

import {logEvent} from "/src/utils/amplitude";
import {css, toSentenceCase} from "/src/utils/utils";
import * as styles from "./card.module.scss";
import translate from "/src/utils/translate";
import {type ButtonKind} from "../../buttons/button/Button";
import LinkTag from "../../buttons/link-tag/LinkTag";
import DefaultPlayerIcon from "/src/svg/video-player-icon.svg";

export type CardFlow = "row" | "column";

export interface CardProps {
  eyebrow?: string;
  headline: string;
  // when video source exists, image prop will be used
  // as video preview image
  image?: ImageObject;
  copy?: string;
  ctas?: Array<CTA | null> | Array<Queries.CtaButtonFragmentFragment | null>;
  darkMode?: boolean;
  videoSource?: string;
  showVideoControls?: boolean;
  portrait?: boolean;
  isImageIcon?: boolean;
  openInNewTab?: boolean;
  useCardStyle?: boolean;
  videoIconVisible?: boolean;
  logo?: ImageObject;
  classes?: string;
  imageClasses?: string;
  gatsbyImageWrapperClasses?: string;
  kind?: ButtonKind;
  flow?: CardFlow;
}

const Card = (props: CardProps): ReactElement => {
  const {
    classes,
    copy,
    ctas = [],
    darkMode = false,
    eyebrow,
    headline,
    image,
    isImageIcon = false,
    kind,
    logo,
    openInNewTab = false,
    portrait = false,
    useCardStyle = false,
    showVideoControls,
    videoIconVisible = false,
    videoSource,
    imageClasses,
    gatsbyImageWrapperClasses,
    flow,
  } = props;

  const isNarrowImage =
    typeof logo?.gatsbyImageData?.width === "number" &&
    logo.gatsbyImageData.width < 300;

  const isFlowRow = flow === "row";

  // Type of firstCta will be (CTA | Queries.CtaButtonFragmentFragment | null)
  const firstCta = ctas && ctas.length > 0 ? ctas[0] : null;

  const handleClick = (
    ctaInput: (CTA | Queries.CtaButtonFragmentFragment) | null | undefined,
    clickSource?: string
  ) => {
    if (!ctaInput) return;

    let propsToLog: Record<string, any> | undefined | null = null;

    // Check if it's a CTA and has amplitudeCtaProps defined
    if ("amplitudeCtaProps" in ctaInput && ctaInput.amplitudeCtaProps) {
      propsToLog = ctaInput.amplitudeCtaProps;
    }

    if (!propsToLog && (ctaInput.amplitudeCtaName || ctaInput.label)) {
      propsToLog = {
        cta_name: ctaInput.amplitudeCtaName || ctaInput.label || "Unknown CTA",
        cta_type:
          ctaInput.amplitudeCtaType ||
          (clickSource === "image" ? "image_link" : "button_link"),
        source_component: "Card",
        ...(clickSource && {click_source_detail: clickSource}),
      };
    }

    if (propsToLog) {
      logEvent("click:marketing_site_cta", propsToLog);
    }
  };

  const buttonKind = kind ? kind : "tertiary";

  const imageFragment =
    image || videoSource ? (
      <div className={css(styles.card__imageWrapper)}>
        {videoSource ? (
          <Video
            portrait={portrait}
            videoTitle={headline || translate("video")}
            videoSource={videoSource}
            showControl={showVideoControls}
            type={VideoType.popup}
            videoThumbnail={image}
            videoConfig={{
              wistia: {
                options: {
                  fitStrategy: "cover",
                },
              },
            }}
            videoClasses={css(styles.card__videoThumbnail, imageClasses!)}
            iconClasses={css(
              styles.card__videoIcon,
              videoIconVisible ? styles.card__videoIconVisible : ""
            )}
          />
        ) : image ? (
          (() => {
            const imageContent = (
              <>
                <Image
                  image={image}
                  imageClasses={css(imageClasses!)}
                  classes={css(
                    styles.card__image,
                    isImageIcon ? styles.card__imageIcon : "",
                    gatsbyImageWrapperClasses!
                  )}
                />
                {videoIconVisible ? (
                  <Icon
                    role="presentation"
                    aria-label={translate("Play Video")}
                    classes={css(
                      styles.card__imageIconOverlay,
                      styles.card__videoIcon,
                      styles.card__videoIconVisible
                    )}
                    svg={DefaultPlayerIcon}
                  />
                ) : null}
              </>
            );

            const canImageBeClickable =
              videoIconVisible &&
              firstCta &&
              typeof firstCta.url === "string" && // .url is string | null for Fragment, string for CTA
              firstCta.url.trim() !== "";

            if (canImageBeClickable) {
              const ctaUrl = firstCta.url as string; // Safe cast due to check above
              // .openInNewTab is boolean | undefined for CTA, boolean | null for Fragment
              const ctaOpenInNewTab = firstCta.openInNewTab ?? openInNewTab;

              return (
                <LinkTag
                  classes={css(styles.card__imageLinkWrapper)}
                  href={ctaUrl}
                  onClick={() => handleClick(firstCta, "image")}
                  openInNewTab={ctaOpenInNewTab}
                >
                  {imageContent}
                </LinkTag>
              );
            } else {
              return imageContent;
            }
          })()
        ) : null}
      </div>
    ) : null;

  const contentFragment = (
    <div
      className={css(
        styles.card__content,
        !copy ? styles.card__contentSmall : ""
      )}
    >
      {logo && (
        <Image
          image={logo}
          objectFit="contain"
          classes={css(
            styles.card__content__logo,
            isNarrowImage ? styles.card__content__logoNarrow : ""
          )}
        />
      )}
      {eyebrow && (
        <p className={css("eyebrow", styles.card__content__eyebrow)}>
          {eyebrow}
        </p>
      )}
      {headline && (
        <h3 className={css("h4", styles.card__content__headline)}>
          <Dotdotdot clamp={6} useNativeClamp tagName="span">
            {headline}
          </Dotdotdot>
        </h3>
      )}
      {copy && (
        <p
          data-name="card-copy"
          className={css("p", styles.card__content__copy)}
        >
          <Dotdotdot clamp={12} useNativeClamp tagName="span">
            {copy}
          </Dotdotdot>
        </p>
      )}
    </div>
  );

  const ctasFragment =
    ctas && ctas.length > 0 && !isFlowRow ? (
      <div className={css(styles.card__ctas)}>
        <ul className={styles.card__ctas__list}>
          {ctas.map(item => {
            if (!item) return null;

            return (
              <li key={uuid()} className={styles.card__ctas__listItem}>
                <CTAButton
                  classes={css(
                    "h5",
                    styles.card__ctas__cta,
                    styles[`card__ctas__cta${toSentenceCase(buttonKind)}`]
                  )}
                  darkMode={darkMode}
                  kind={buttonKind as ButtonKind}
                  small={buttonKind != "tertiary"}
                  cta={item as CTA}
                  onClick={() => handleClick(item, "button")}
                  openInNewTab={item.openInNewTab ?? openInNewTab}
                />
              </li>
            );
          })}
        </ul>
      </div>
    ) : null;

  const cardChildren = (
    <div
      className={css(
        styles.card,
        useCardStyle ? styles.cardCardStyle : "",
        darkMode ? styles.cardDark : "",
        isFlowRow ? styles.cardFlowRow : "",
        classes!
      )}
    >
      {imageFragment}
      {contentFragment}
      {ctasFragment}
    </div>
  );

  if (
    isFlowRow &&
    firstCta &&
    typeof firstCta.url === "string" &&
    firstCta.url.trim() !== ""
  ) {
    const ctaUrl = firstCta.url as string;
    const ctaOpenInNewTab = firstCta.openInNewTab ?? openInNewTab;
    return (
      <LinkTag
        classes={styles.card__linkWrapper}
        href={ctaUrl}
        onClick={() => handleClick(firstCta, "card_row_link")}
        openInNewTab={ctaOpenInNewTab}
      >
        {cardChildren}
      </LinkTag>
    );
  }

  return <>{cardChildren}</>;
};

export default Card;
